/**
 * Story `fe-be-monteur-s5d-offline-en-achtergrond` (S021.5d) §A.1, §A.2, §A.4 — de pure regels:
 * houdbaarheid (PB-3), de bannertijd, "is dit offline?", en de twee beslisfuncties
 * `offlineDisplayDecision` (DT-SHOW) en `coldStartDecision` (DT-COLD).
 *
 * Geen React, geen netwerk, geen `Date.now()`: de klok komt altijd als argument binnen.
 */
import { ApiError, NotAuthenticatedError } from '@carculate/api-client';

import type { OfflineRecord } from './types';

// ─── De klok (PB-3, A-4) ─────────────────────────────────────────────────────

/** Een `storedAt` mag hooguit zoveel in de toekomst liggen (klok iets verzet), daarna ongeldig. */
export const MAX_FUTURE_SKEW_MS = 5 * 60 * 1000;

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/** De lokale kalenderdag van het toestel als `yyyy-MM-dd`. */
export function deviceDay(ms: number): string {
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Zelfde lokale kalenderdag van het toestel? (PB-3: "vandaag" = de toesteldag.) */
export function isSameDeviceDay(a: number, b: number): boolean {
    return deviceDay(a) === deviceDay(b);
}

/**
 * Mag een record met deze `storedAt` NU nog getoond worden? Zelfde toesteldag én niet meer dan
 * 5 minuten in de toekomst. Een ontbrekende of onzinnige tijd is ongeldig.
 */
export function isStoredAtValid(storedAt: unknown, now: number): boolean {
    if (typeof storedAt !== 'number' || !Number.isFinite(storedAt)) return false;
    if (storedAt - now > MAX_FUTURE_SKEW_MS) return false;
    return isSameDeviceDay(storedAt, now);
}

/** De bannertijd: lokale tijd, 24-uurs, met voorloopnul ("08:05", "00:00"). */
export function clockLabel(ms: number): string {
    const d = new Date(ms);
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// ─── Is dit "geen verbinding"? (A-2) ─────────────────────────────────────────

/**
 * Een fout zonder HTTP-antwoord: `fetch` gooit dan een `TypeError`. Een `AbortError`
 * (wegnavigeren) is GEEN offline, en elke `ApiError` evenmin: dan heeft de server gesproken.
 */
export function isNetworkError(err: unknown): boolean {
    if (err instanceof ApiError) return false;
    return err instanceof TypeError;
}

/** Een geannuleerde fetch (wegnavigeren). */
export function isAbortError(err: unknown): boolean {
    return (
        err !== null &&
        typeof err === 'object' &&
        (err as { name?: unknown }).name === 'AbortError'
    );
}

// ─── DT-SHOW (§A.2) ──────────────────────────────────────────────────────────

/**
 * De uitkomst voor één monteur-scherm. `loading` = nog geen beslissing (online aan het laden, een
 * `AbortError`, of de bewaarde gegevens worden nog gelezen).
 */
export type OfflineDisplayOutcome = 'live' | 'snapshot' | 'not-available' | 'server-error' | 'login' | 'loading';

export interface OfflineDisplayInput {
    /** `navigator.onLine` (via `useFitterOffline`). */
    online: boolean;
    /** De TanStack-`fetchStatus` van de query. */
    fetchStatus: 'fetching' | 'paused' | 'idle';
    /** Zijn er gegevens in het geheugen (React Query)? */
    hasData: boolean;
    /** De fout, of de laatste fout tijdens het herhalen (`failureReason`). */
    error: unknown;
    /**
     * Een geldige bewaarde stand: `valid` / `none` (ook: verlopen, van een ander, geen soort),
     * `unknown` = nog niet gelezen.
     */
    snapshot: 'valid' | 'none' | 'unknown';
}

export interface OfflineDisplayDecision {
    outcome: OfflineDisplayOutcome;
    /** De offline-banner tonen? Hangt aan de TOESTAND, niet aan de bron (A-12). */
    banner: boolean;
    /** Moet de bewaarde stand gelezen worden? (alleen bij offline zonder geheugen) */
    needsSnapshot: boolean;
}

/**
 * DT-SHOW. Volgorde = prioriteit:
 *  1. 401 → `login`, nooit de bewaarde stand (ook niet met gegevens in het geheugen).
 *  2. Elke andere server-fout (`ApiError`, ook `ResumeUnavailableError`): met gegevens in het
 *     geheugen blijft het scherm zoals het was (`live`, geen banner); zonder → `server-error`.
 *  3. Offline (`onLine` false, `paused`, of een `TypeError`): geheugen → `live` + banner;
 *     anders de bewaarde stand → `snapshot` + banner; anders `not-available` + banner.
 *  4. Gegevens in het geheugen → `live`.
 *  5. Anders: `loading` (ook bij een `AbortError` of een andere fout zonder antwoord... die
 *     laatste valt onder 2 als hij een gewone `Error` is: bij twijfel niets tonen).
 */
export function offlineDisplayDecision(input: OfflineDisplayInput): OfflineDisplayDecision {
    const { error } = input;

    if (error instanceof NotAuthenticatedError) return { outcome: 'login', banner: false, needsSnapshot: false };

    if (error instanceof ApiError) {
        return { outcome: input.hasData ? 'live' : 'server-error', banner: false, needsSnapshot: false };
    }

    const offline = !input.online || input.fetchStatus === 'paused' || isNetworkError(error);
    if (offline) {
        if (input.hasData) return { outcome: 'live', banner: true, needsSnapshot: false };
        if (input.snapshot === 'valid') return { outcome: 'snapshot', banner: true, needsSnapshot: false };
        if (input.snapshot === 'unknown') return { outcome: 'loading', banner: false, needsSnapshot: true };
        return { outcome: 'not-available', banner: true, needsSnapshot: false };
    }

    if (input.hasData) return { outcome: 'live', banner: false, needsSnapshot: false };

    // Een geannuleerde lading is geen offline: blijven laden (R11).
    if (error === null || error === undefined || isAbortError(error)) {
        return { outcome: 'loading', banner: false, needsSnapshot: false };
    }

    // Een andere fout (geen HTTP-antwoord, geen TypeError): bij twijfel de bestaande foutstaat.
    return { outcome: 'server-error', banner: false, needsSnapshot: false };
}

// ─── DT-COLD (§A.4) ──────────────────────────────────────────────────────────

/** Hoe whoami afliep bij het openen. */
export type ColdWhoAmI = 'ok' | 'login-required' | 'offline';

export interface ColdStartInput {
    whoami: ColdWhoAmI;
    /** De gebruiker uit whoami (alleen bij `ok`). */
    userId: string | null;
    /** `isLoggedOut()` (S021.5c). */
    loggedOut: boolean;
    owner: { userId: string; verifiedAt: number } | null;
    /** Zijn er bewaarde gegevens van de eigenaar? */
    hasSnapshot: boolean;
    now: number;
}

export type ColdStartOutcome = 'online' | 'login' | 'offline' | 'no-connection';

export interface ColdStartDecision {
    outcome: ColdStartOutcome;
    /** Moet de offline-cache leeg (G-4 bij login-required, W5 bij een andere eigenaar, C5 bij gisteren)? */
    wipe: boolean;
}

/** DT-COLD: wat de monteur-app toont als hij opent. Nooit het inlogscherm zonder verbinding. */
export function coldStartDecision(input: ColdStartInput): ColdStartDecision {
    if (input.whoami === 'ok') {
        const otherOwner = input.owner !== null && input.owner.userId !== input.userId;
        return { outcome: 'online', wipe: otherOwner };
    }
    if (input.whoami === 'login-required') return { outcome: 'login', wipe: true };

    // Zonder verbinding: nooit het inlogscherm (offline kan niemand inloggen).
    if (input.loggedOut) return { outcome: 'no-connection', wipe: false };
    if (input.owner === null) return { outcome: 'no-connection', wipe: false };
    if (!isSameDeviceDay(input.owner.verifiedAt, input.now) || input.owner.verifiedAt - input.now > MAX_FUTURE_SKEW_MS) {
        return { outcome: 'no-connection', wipe: true };
    }
    if (!input.hasSnapshot) return { outcome: 'no-connection', wipe: false };
    return { outcome: 'offline', wipe: false };
}

// ─── Een record nog tonen? ───────────────────────────────────────────────────

/**
 * De toets bij het LEZEN (twee sloten met de generatie): juiste eigenaar, schemaversie, vandaag,
 * niet te ver in de toekomst. De vormtoets per soort staat in `project.ts`.
 */
export function isRecordCurrent(record: Pick<OfflineRecord, 'userId' | 'storedAt' | 'schema'>, userId: string, now: number, schema: number): boolean {
    if (record.userId !== userId) return false;
    if (record.schema !== schema) return false;
    return isStoredAtValid(record.storedAt, now);
}
